import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { api, API, session } from "@/lib/api";

const AuthCtx = createContext(null);
const BACKUP = "orbis_platform_backup";

export function AuthProvider({ children }) {
  const [state, setState] = useState({ status: "loading", me: null });

  const load = useCallback(async () => {
    const s = session.get();
    if (!s?.access_token) return setState({ status: "anon", me: null });
    try {
      const url = s.scope === "platform" ? "/platform/me" : "/auth/me";
      const { data } = await api.get(url);
      setState({ status: s.scope === "platform" ? "platform" : "tenant", me: data });
      return data;
    } catch (e) {
      if (e.response?.status === 401) session.clear();
      setState({ status: "anon", me: null });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loginWithTokens = async (tokens) => { session.set(tokens); return load(); };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    const backup = localStorage.getItem(BACKUP);
    session.clear(); navigator.serviceWorker?.controller?.postMessage({ type: "clear-api-cache" });
    localStorage.removeItem(BACKUP);
    if (backup) { session.set(JSON.parse(backup)); }
    await load();
  };

  const switchCompany = async (company_id) => {
    const { data } = await api.post("/auth/switch-company", { company_id });
    session.set({ ...session.get(), ...data });
    await load();
  };

  const switchTenant = async (tenant_id) => {
    const { data } = await api.post("/auth/switch-tenant", { tenant_id });
    session.set({ ...session.get(), ...data });
    await load();
  };

  const startImpersonation = async (tokens) => {
    localStorage.setItem(BACKUP, JSON.stringify(session.get()));
    session.set({ ...tokens, scope: "tenant" });
    await load();
  };

  const exitImpersonation = async () => {
    const backup = JSON.parse(localStorage.getItem(BACKUP) || "null");
    const impId = state.me?.impersonation?.id;
    if (backup && impId) {
      try { await axios.post(`${API}/platform/impersonate/${impId}/stop`, {}, { headers: { Authorization: `Bearer ${backup.access_token}` } }); } catch (e) { /* ignore */ }
    }
    localStorage.removeItem(BACKUP);
    session.clear();
    if (backup) session.set(backup);
    await load();
  };

  const can = (module, level) => !!state.me?.permissions?.[module]?.includes(level);

  return <AuthCtx.Provider value={{ ...state, reload: load, loginWithTokens, logout, switchCompany, switchTenant, startImpersonation, exitImpersonation, can }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
